import * as Phaser from 'phaser';
import { DESIGN } from '../config/design';
import { ToySprite } from '../objects/ToySprite';
import { StyledButton } from '../objects/StyledButton';
import { getVisualForToy } from '../config/toys';
import { soundManager } from '../systems/SoundManager';
import type { ToyConfig } from '../types';

export class LegendaryRevealScene extends Phaser.Scene {
  private toy!: ToyConfig;

  constructor() {
    super({ key: 'LegendaryRevealScene' });
  }

  init(data: { toy: ToyConfig }): void {
    this.toy = data.toy;
  }

  create(): void {
    this.createBackground();
    this.createRadiatingLight();
    this.createConfetti();
    this.createHeader();
    this.createToyReveal();
    this.createStatsCard();
    this.createActions();
  }

  private createBackground(): void {
    const graphics = this.add.graphics();
    for (let y = 0; y < 720; y += 4) {
      const t = y / 720;
      const r = Math.floor(248 + (232 - 248) * t);
      const g = Math.floor(249 + (240 - 249) * t);
      const b = Math.floor(255 + (244 - 255) * t);
      graphics.fillStyle((r << 16) | (g << 8) | b, 1);
      graphics.fillRect(0, y, 1280, 4);
    }
  }

  private createRadiatingLight(): void {
    const rays = this.add.graphics();
    const colors = [0xffe173, 0xffffff];

    for (let i = 0; i < 24; i++) {
      const angle = (i / 24) * Math.PI * 2;
      const nextAngle = ((i + 1) / 24) * Math.PI * 2;
      const color = colors[i % 2];
      const alpha = i % 2 === 0 ? 0.3 : 0.15;
      rays.fillStyle(color, alpha);
      rays.beginPath();
      rays.moveTo(640, 360);
      rays.arc(640, 360, 1200, angle, nextAngle);
      rays.closePath();
      rays.fillPath();
    }

    rays.setDepth(-1);

    this.tweens.add({
      targets: rays,
      rotation: Math.PI * 2,
      duration: 20000,
      repeat: -1,
      ease: 'Linear',
    });

    const glow = this.add.graphics();
    glow.fillStyle(0xffe173, 0.4);
    glow.fillCircle(640, 360, 300);
    glow.setDepth(-2);

    this.tweens.add({
      targets: glow,
      scale: 1.1,
      alpha: 0.6,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private createHeader(): void {
    const title = this.add.text(640, 90, 'LEGENDARY CATCH!', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '56px',
      color: DESIGN.colors.legendary,
      fontStyle: 'bold',
    }).setOrigin(0.5);

    title.setShadow(0, 4, '#473a00', 0, false, true);

    this.tweens.add({
      targets: title,
      scale: 1.05,
      duration: 600,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    const badge = this.add.container(640, 150);
    const badgeBg = this.add.rectangle(0, 0, 260, 34, 0xc3a300);
    badge.add(badgeBg);
    badge.add(this.add.text(0, 0, '★ RARE TREASURE UNLOCKED', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '14px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5));
  }

  private createToyReveal(): void {
    const container = this.add.container(640, 340);

    const glow = this.add.graphics();
    glow.fillStyle(0xffe173, 0.3);
    glow.fillCircle(0, 0, 200);
    container.add(glow);

    const visual = getVisualForToy(this.toy.id);
    const toy = new ToySprite(this, 0, 0, visual, 160);
    container.add(toy);

    this.tweens.add({
      targets: toy,
      y: -20,
      rotation: 0.05,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    this.createSparkles(container);

    this.tweens.add({
      targets: container,
      scale: { from: 0, to: 1 },
      duration: 800,
      ease: 'Back.easeOut',
    });
  }

  private createSparkles(parent: Phaser.GameObjects.Container): void {
    const positions = [
      { x: -140, y: -80, delay: 0.2 },
      { x: 140, y: -40, delay: 0.8 },
      { x: -120, y: 60, delay: 1.5 },
      { x: 120, y: 80, delay: 0.5 },
    ];

    positions.forEach((pos) => {
      const sparkle = this.add.text(pos.x, pos.y, '✦', {
        fontSize: '32px',
        color: '#ffe173',
      }).setOrigin(0.5);
      parent.add(sparkle);

      this.tweens.add({
        targets: sparkle,
        scale: { from: 0.5, to: 1.2 },
        alpha: { from: 0, to: 1 },
        duration: 1000,
        delay: pos.delay * 1000,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    });
  }

  private createStatsCard(): void {
    const container = this.add.container(640, 520);

    const shadow = this.add.rectangle(6, 6, 360, 110, 0x000000, 0.1);
    container.add(shadow);

    const bg = this.add.rectangle(0, 0, 360, 110, 0xffffff, 0.9);
    bg.setStrokeStyle(2, 0xffe173);
    container.add(bg);

    container.add(this.add.text(-140, -25, 'COLLECTION NO. 001', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '12px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    container.add(this.add.text(-140, 5, this.toy.name, {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '24px',
      color: DESIGN.colors.legendary,
      fontStyle: 'bold',
    }).setOrigin(0, 0.5));

    const rarityBadge = this.add.rectangle(130, 0, 48, 48, 0xc3a300);
    container.add(rarityBadge);
    container.add(this.add.text(130, 0, 'L', {
      fontFamily: DESIGN.fonts.headline,
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5));

    this.tweens.add({
      targets: container,
      scale: { from: 0.5, to: 1 },
      duration: 500,
      delay: 200,
      ease: 'Back.easeOut',
    });
  }

  private createActions(): void {
    const description = this.add.text(640, 600, 'You\'ve discovered one of the rarest creatures in the Toybox Arcade universe. This legendary friend grants double luck on your next 5 pulls!', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '16px',
      color: DESIGN.colors.onSurfaceVariant,
      align: 'center',
      wordWrap: { width: 560 },
    }).setOrigin(0.5);

    const claimBtn = new StyledButton(this, 640, 660, '✨ CLAIM', {
      width: 280,
      height: 60,
      bgColor: parseInt(DESIGN.colors.primary.replace('#', '0x'), 16),
      shadowColor: parseInt(DESIGN.colors.primaryDark.replace('#', '0x'), 16),
      fontSize: '22px',
      onClick: () => this.onClaim(),
    });

    this.tweens.add({
      targets: [description, claimBtn],
      scale: { from: 0.5, to: 1 },
      duration: 500,
      delay: 400,
      ease: 'Back.easeOut',
    });

    const share = this.add.text(640, 710, '🔗 SHARE WITH FRIENDS', {
      fontFamily: DESIGN.fonts.body,
      fontSize: '14px',
      color: DESIGN.colors.onSurfaceVariant,
      fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });

    share.on('pointerdown', () => {
      soundManager.play('click');
      if (typeof navigator !== 'undefined' && navigator.share) {
        navigator.share({
          title: 'Toybox Arcade - Legendary Catch!',
          text: `I just caught ${this.toy.name} in Toybox Arcade!`,
          url: typeof window !== 'undefined' ? window.location.href : '',
        }).catch(() => {});
      }
    });
  }

  private createConfetti(): void {
    const colors = [0xa43a3d, 0xffe173, 0x006b5f, 0xff7e7e, 0x88f2e0];

    for (let i = 0; i < 60; i++) {
      const color = colors[Math.floor(Math.random() * colors.length)];
      const x = Math.random() * 1280;
      const y = -20 - Math.random() * 200;
      const size = 4 + Math.random() * 8;
      const particle = this.add.rectangle(x, y, size, size, color);
      particle.setRotation(Math.random() * Math.PI);

      this.tweens.add({
        targets: particle,
        y: 740 + Math.random() * 100,
        x: x + (Math.random() - 0.5) * 400,
        rotation: Math.random() * Math.PI * 4,
        alpha: 0,
        duration: 2000 + Math.random() * 3000,
        delay: Math.random() * 1000,
        repeat: -1,
      });
    }
  }

  private onClaim(): void {
    soundManager.play('click');
    this.scene.start('GameScene');
  }
}
